import React, { useState, forwardRef, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Container,
    Link,
    TextField,
    FormControl,
    FormHelperText,
    InputLabel,
    Button,
    Paper,
    InputAdornment,
    IconButton,
    CircularProgress,
    Select,
    FormControlLabel,
    Checkbox,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Autocomplete as MuiAutocomplete
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useThemeContext, THEME_CLASSIC, THEME_DARK } from '../theme/ThemeContext';
import { useTheme, alpha } from '@mui/material/styles';
import { useUser } from '../components/userContext';
import MenuItem from '@mui/material/MenuItem';
import DrkMultiFilterSelect from './DrkMultiFilterSelect';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { getPasswordRequirementStatus } from '../misc/helperFunctions';

/**
 * Hilfsfunktion: Liefert Styling für aktive Filter und Suchfelder
 * @param {boolean} isActive - Gibt an, ob der Filter aktiv ist
 * @param {boolean} isClassic - Gibt an, ob das klassische Theme aktiv ist
 * @returns {Object} - Style-Objekt für den aktiven Filter
 */
export const getActiveFilterStyle = (isActive, isClassic) => ({
    backgroundColor: isActive
        ? isClassic
            ? 'rgba(91, 155, 213, 0.1)' // Helleres Blau für Classic
            : 'rgba(228, 100, 80, 0.1)' // Leichtes Orange für Modern
        : 'inherit',
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isActive ? 'primary.main' : 'lightgray'
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isActive ? 'primary.main' : 'lightgray'
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: 'primary.main'
    }
});

/**
 * DRK-PageContainer - Hauptcontainer für alle Seiten
 */
export const DrkPageContainer = ({ children, maxWidth = "lg" }) => (
    <Container maxWidth={maxWidth} sx={{ py: 5 }}>
        {children}
    </Container>
);

/**
 * DrkCard - Basiskarte mit DRK-Styling
 */
export const DrkCard = ({ children, title, subtitle, maxWidth, elevation = 2, ...props }) => {
    const { currentTheme } = useThemeContext();

    // Theme-spezifisches Styling
    const isClassic = currentTheme === THEME_CLASSIC;
    const isDark = currentTheme === THEME_DARK;

    return (
        <Card
            elevation={elevation}
            sx={{
                maxWidth: maxWidth || '650px',
                mx: 'auto',
                borderRadius: isClassic ? '8px' : '16px', // Leicht vergrößerter Radius im klassischen Theme
                overflow: 'hidden',
                bgcolor: isDark ? '#121212' : 'background.paper',
                backgroundImage: isDark ? 'none' : undefined,
                ...props.sx
            }}
        >
            {/* Farbiger Balken am oberen Rand - jetzt in beiden Themes anzeigen */}
            <Box sx={{
                height: isClassic ? '6px' : '8px', // Etwas schmaler im klassischen Theme
                bgcolor: isClassic ? 'designBlue.main' : 'primary.main' // Helleres Blau für Classic-Theme
            }} />

            <CardContent sx={{ p: isClassic ? { xs: 2, sm: 3 } : { xs: 2, sm: 4 } }}>
                {title && (
                    <Typography
                        variant="h5"
                        gutterBottom
                        textAlign="center"
                        fontWeight={isClassic ? "500" : "500"} // Jetzt gleiche Dicke
                        color="primary"
                        sx={{ mb: subtitle ? 1 : 4 }}
                    >
                        {title}
                    </Typography>
                )}
                {subtitle && (
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        align="center"
                        sx={{ mb: 4 }}
                    >
                        {subtitle}
                    </Typography>
                )}

                {children}
            </CardContent>
        </Card>
    );
};

/**
 * DrkIconCircle - Runder Icon-Container
 */
export const DrkIconCircle = ({ icon, color = "primary", size = "medium", sx = {} }) => {
    // Größenoptionen
    const sizes = {
        small: { circle: 50, icon: 24 },
        medium: { circle: 60, icon: 30 },
        large: { circle: 80, icon: 45 },
    };

    const { circle, icon: iconSize } = sizes[size] || sizes.medium;
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Mappe Farbschlüssel auf Theme-Tokens (inkl. success/info/error)
    const resolveMain = (key) => {
        const pal = theme.palette;
        if (pal[key] && pal[key].main) return pal[key].main;
        // Fallback auf primary
        return pal.primary.main;
    };

    const mainColor = resolveMain(color);
    const bgColor = alpha(mainColor, isDark ? 0.15 : 0.10);
    const iconColor = mainColor;

    return (
        <Box sx={{
            width: circle,
            height: circle,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: bgColor,
            mb: 2,
            ...sx
        }}>
            {React.cloneElement(icon, { sx: { fontSize: iconSize, color: iconColor } })}
        </Box>
    );
};

/**
 * DrkInfoBox - Box für Informationstexte mit leichtem Hintergrund
 */
export const DrkInfoBox = ({ children, color = "primary", sx = {} }) => {
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;

    // Theme-spezifisches Styling für Hintergrundfarbe und Border-Radius
    const bgColor = isClassic
        ? color === "primary"
            ? 'rgba(91, 155, 213, 0.05)' // Design-Blau im klassischen Theme als Hintergrund beibehalten
            : color === "secondary"
                ? 'rgba(228, 100, 80, 0.05)' // DRK-Rot als sekundär
                : 'rgba(0, 0, 0, 0.03)'
        : color === "primary"
            ? 'rgba(228, 100, 80, 0.05)'
            : color === "secondary"
                ? 'rgba(76, 175, 80, 0.05)'
                : 'rgba(0, 0, 0, 0.03)';

    return (
        <Box
            sx={{
                py: 3,
                px: 2,
                bgcolor: bgColor,
                borderRadius: isClassic ? '8px' : '12px', // Vergrößerter Radius im klassischen Theme
                textAlign: 'center',
                ...sx
            }}
        >
            {children}
        </Box>
    );
};

/**
 * DrkLoginLink - Standardisierter Link zur Anmeldeseite
 */
export const DrkLoginLink = ({ text = "Zurück zur Anmeldung" }) => (
    <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Link
            href="/login"
            sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontWeight: 'medium',
                '&:hover': {
                    textDecoration: 'underline'
                },
                display: 'inline-flex',
                alignItems: 'center'
            }}
        >
            {text}
        </Link>
    </Box>
);

/**
 * DrkDialogCard - Spezialisierte Kartenkomponente für Dialoge
 * Optimiert für die Verwendung innerhalb von Material-UI-Dialogen
 */
export const DrkDialogCard = ({ children, title, subtitle, ...props }) => {
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;

    return (
        <Box
            sx={{
                borderRadius: isClassic ? '8px' : '8px', // Jetzt gleicher Radius
                overflow: 'hidden',
                ...props.sx
            }}
        >
            {/* Farbiger Balken am oberen Rand - jetzt in beiden Themes anzeigen */}
            {props.showHeaderBar && (
                <Box sx={{
                    height: isClassic ? '4px' : '4px',
                    bgcolor: isClassic ? 'designBlue.main' : 'primary.main' // Helleres Blau für Classic-Theme
                }} />
            )}
            <Box sx={{ p: isClassic ? { xs: 1.5, sm: 2.5 } : { xs: 1.5, sm: 2.5 } }}> {/* Angepasste Polsterung */}
                {title && (
                    <Typography
                        variant="h6"
                        gutterBottom
                        fontWeight={isClassic ? "500" : "500"} // Jetzt gleiche Dicke
                        color="primary"
                        sx={{ mb: subtitle ? 1 : 2 }}
                    >
                        {title}
                    </Typography>
                )}
                {subtitle && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        {subtitle}
                    </Typography>
                )}

                {children}
            </Box>
        </Box>
    );
};

/**
 * DrkDataGrid - Angepasste DataGrid-Komponente mit PersistentDataGrid-Funktionalität
 * Erweitert die MUI DataGrid mit persistentem Speicher (analog zu PersistentDataGrid)
 */
export const DrkDataGrid = ({
    rows,
    columns,
    storageKey,
    loading = false,
    error = null,
    emptyText = "Keine Daten verfügbar",
    onRowClick,
    onColumnWidthChange: externalColumnWidthChange,
    ...props
}) => {
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;
    const isDark = currentTheme === THEME_DARK;
    // eslint-disable-next-line no-unused-vars
    const theme = useTheme();
    const { settings, updateSetting } = useUser();

    // Auslesen der persistierten Breiten
    const storedWidths = useMemo(() => settings[storageKey] || {}, [settings, storageKey]);

    // Mergen der übergebenen Spalten mit den persistierten Breiten
    const mergedColumns = useMemo(() => {
        return columns.map((col) => ({
            ...col,
            width: storedWidths[col.field] || col.width || 150,
        }));
    }, [columns, storedWidths]);

    // Handler für Spaltenbreitenänderung
    const handleColumnWidthChange = (params, event) => {
        const { colDef, width } = params;
        // Aktualisierte Breiten speichern
        const updatedWidths = { ...storedWidths, [colDef.field]: width };
        updateSetting(storageKey, updatedWidths);

        // Falls ein externer Handler existiert, diesen auch aufrufen
        if (externalColumnWidthChange) {
            externalColumnWidthChange(params, event);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                width: '100%',
                mb: 2,
                borderRadius: isClassic ? 2 : 2, // Gleicher Radius für beide Themes
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                bgcolor: isDark ? '#121212' : 'background.paper',
                backgroundImage: isDark ? 'none' : undefined
            }}
        >
            {error && (
                <Box sx={{ p: 2, color: 'error.main', bgcolor: 'error.light' }}>
                    <Typography variant="body2">{error}</Typography>
                </Box>
            )}

            <DataGrid
                rows={rows}
                columns={mergedColumns} // Verwenden der gemergten Spalten
                onColumnWidthChange={handleColumnWidthChange}
                autoHeight
                disableSelectionOnClick
                loading={loading}
                onRowClick={onRowClick}
                sx={{
                    bgcolor: isDark ? '#121212' : 'transparent',
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: isDark
                            ? 'rgba(255, 255, 255, 0.05)'
                            : isClassic
                                ? 'rgba(91, 155, 213, 0.05)' // Design-Blau als Hintergrund beibehalten
                                : 'rgba(228, 100, 80, 0.05)',
                        fontWeight: 'bold',
                    },
                    '& .MuiDataGrid-cell:focus': {
                        outline: 'none',
                    },
                    '& .MuiDataGrid-row:hover': {
                        backgroundColor: isDark
                            ? 'rgba(255, 255, 255, 0.08)'
                            : isClassic
                                ? 'rgba(91, 155, 213, 0.03)' // Design-Blau als Hintergrund beibehalten
                                : 'rgba(228, 100, 80, 0.03)',
                    },
                    ...props.sx
                }}
                localeText={{
                    noRowsLabel: emptyText,
                    footerRowSelected: (count) => `${count} Zeile${count !== 1 ? 'n' : ''} ausgewählt`,
                }}
                {...props}
            />
        </Paper>
    );
};

/**
 * DrkFormField - Formularfeld-Komponente mit einheitlichem Styling
 */
export const DrkFormField = forwardRef(({
    label,
    type = 'text',
    error,
    helperText,
    fullWidth = true,
    required = false,
    options = [], // Für Select-Felder
    value,
    onChange,
    ...props
}, ref) => {
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;
    const borderRadius = isClassic ? '8px' : '8px'; // Jetzt gleicher Radius

    // Basierend auf dem Typ des Feldes rendern wir unterschiedliche Komponenten
    switch (type) {
        case 'select':
            return (
                <FormControl
                    fullWidth={fullWidth}
                    error={!!error}
                    required={required}
                    margin="normal"
                    {...props}
                >
                    <InputLabel>{label}</InputLabel>
                    <Select
                        value={value}
                        onChange={onChange}
                        label={label}
                        ref={ref}
                        sx={{ borderRadius }}
                    >
                        {options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {(error || helperText) && (
                        <FormHelperText>{error || helperText}</FormHelperText>
                    )}
                </FormControl>
            );

        case 'checkbox':
            return (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={value}
                            onChange={onChange}
                            ref={ref}
                            {...props}
                        />
                    }
                    label={label}
                />
            );

        // Für alle anderen Typen (text, password, email, etc.) verwenden wir TextField
        default:
            return (
                <TextField
                    label={label}
                    type={type}
                    fullWidth={fullWidth}
                    error={!!error}
                    helperText={error || helperText}
                    required={required}
                    margin="normal"
                    value={value}
                    onChange={onChange}
                    InputProps={{
                        sx: { borderRadius }
                    }}
                    ref={ref}
                    {...props}
                />
            );
    }
});

/**
 * DrkActionBar - Standardisierte Komponente für Aktionsleisten
 */
export const DrkActionBar = ({
    searchTerm = '',
    onSearchChange,
    actions = [],
    filters = [],
    showSearch = true,
    searchPlaceholder = "Suchen...",
    sx = {}
}) => {
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;

    // Handling für die Suche
    const handleSearchChange = (value) => {
        if (onSearchChange) {
            onSearchChange(value);
        }
    };

    // Handling für das Löschen der Suche
    const handleClearSearch = () => {
        if (onSearchChange) {
            onSearchChange('');
        }
    };

    return (
        <Box>
            <Paper
                elevation={0}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1,
                    mb: filters.length > 0 ? 1 : 2,
                    borderRadius: isClassic ? 2 : 2,
                    bgcolor: 'background.paper',
                    ...sx
                }}
            >
                {showSearch && (
                    <DrkSearchField
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder={searchPlaceholder}
                        sx={{ width: { xs: '60%', sm: '70%' } }}
                    />
                )}

                {/* Actions-Bereich für Buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {actions.map((action, index) => (
                        <Button
                            key={index}
                            variant={action.variant || "contained"}
                            color={action.color || "primary"}
                            startIcon={action.icon}
                            onClick={action.onClick}
                            disabled={action.disabled}
                            size={action.size || "medium"}
                        >
                            {action.label}
                        </Button>
                    ))}
                </Box>
            </Paper>

            {/* Filter-Chips Bereich */}
            {filters.length > 0 && (
                <Paper
                    elevation={0}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        mb: 2,
                        borderRadius: isClassic ? 2 : 2,
                        bgcolor: 'background.paper',
                    }}
                >
                    <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
                        Filter:
                    </Typography>
                    {filters.map((filter, index) => (
                        <FormControlLabel
                            key={index}
                            control={
                                <Checkbox
                                    checked={filter.checked}
                                    onChange={filter.onChange}
                                    size="small"
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2">{filter.label}</Typography>
                            }
                        />
                    ))}
                </Paper>
            )}
        </Box>
    );
};

/**
 * DrkPreviewFrame - Spezieller Container für Vorschau-Inhalte
 */
export const DrkPreviewFrame = ({
    html,
    height = '600px',
    loading = false,
    error = null,
    showCopyButton = true,
    onCopy,
    sx = {}
}) => {
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;

    // Handling für das Kopieren des HTML-Codes
    const handleCopy = () => {
        if (html && onCopy) {
            onCopy(html);
        } else if (html) {
            navigator.clipboard.writeText(html);
        }
    };

    return (
        <Box sx={{ position: 'relative', ...sx }}>
            {/* Fehleranzeige */}
            {error && (
                <Box
                    sx={{
                        p: 2,
                        color: 'error.main',
                        bgcolor: 'error.light',
                        borderRadius: isClassic ? 2 : 2, // Gleicher Radius für beide Themes
                        mb: 2
                    }}
                >
                    <Typography variant="body2">{error}</Typography>
                </Box>
            )}

            {/* Loading-Indikator */}
            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: height,
                        bgcolor: '#f9f9f9',
                        borderRadius: isClassic ? 2 : 2, // Gleicher Radius für beide Themes
                        border: '1px solid #ddd'
                    }}
                >
                    <CircularProgress size={40} />
                </Box>
            ) : (
                <Box
                    sx={{
                        border: '1px solid #ddd',
                        borderRadius: isClassic ? 2 : 2, // Gleicher Radius für beide Themes
                        overflow: 'hidden',
                        height: height,
                        position: 'relative'
                    }}
                >
                    {/* Copy-Button */}
                    {showCopyButton && html && (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopy}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 2,
                                opacity: 0.9,
                                '&:hover': {
                                    opacity: 1
                                }
                            }}
                        >
                            Kopieren
                        </Button>
                    )}

                    {/* Der eigentliche Frame mit dem HTML-Inhalt */}
                    <iframe
                        srcDoc={html}
                        title="Vorschau"
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                    />
                </Box>
            )}
        </Box>
    );
};

/**
 * DrkDialog - Spezieller moderner Dialog mit DRK-Styling, 
 * inspiriert vom AvailabilityCheckDialog
 */
export const DrkDialog = ({
    open,
    onClose,
    title,
    children,
    actions,
    maxWidth = "md",
    fullWidth = true,
    headerActions = null,
    headerSx = {},
    ...props
}) => {
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;

    // Funktion zum Rendern des Titels, abhängig vom Theme
    const renderTitle = () => {
        // Wenn es ein einfacher String-Titel ist
        if (typeof title === 'string') {
            return <Typography variant="inherit">{title}</Typography>;
        }
        // Wenn title bereits ein JSX-Element ist, direkt verwenden
        return title;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            PaperProps={{
                sx: {
                    borderRadius: isClassic ? '8px' : '12px', // Leicht vergrößerter Radius im klassischen Theme
                    overflow: 'hidden'
                }
            }}
            {...props}
        >
            {/* Beide Themes nutzen jetzt farbige Titelleisten */}
            <DialogTitle sx={(theme) => ({
                backgroundColor: isClassic ? 'designBlue.main' : 'primary.main',
                backgroundImage: isClassic
                    ? 'none'
                    : theme.palette.mode === 'dark'
                        ? 'linear-gradient(180deg, #eb5b28 0%, #f28424 100%)'
                        : 'none',
                color: '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                ...(typeof headerSx === 'function' ? headerSx(theme) : headerSx)
            })}>
                {renderTitle()}
                {headerActions && (
                    <Box>
                        {headerActions}
                    </Box>
                )}
            </DialogTitle>

            <DialogContent sx={(theme) => ({
                mt: 2,
                p: isClassic ? 2.5 : 3,
                pt: 2,
                bgcolor: theme.palette.background.paper
            })}>
                {children}
            </DialogContent>

            {actions && (
                <DialogActions sx={(theme) => ({
                    justifyContent: 'flex-end',
                    p: 2,
                    bgcolor: theme.palette.background.paper,
                    borderTop: 1,
                    borderColor: 'divider'
                })}>
                    {actions}
                </DialogActions>
            )}
        </Dialog>
    );
};

/**
 * DrkConfirmationDialog - Generischer Bestätigungsdialog für verschiedene Aktionen
 */
export const DrkConfirmationDialog = ({
    open,
    onClose,
    onConfirm,
    title,
    description,
    confirmButtonText = "Ja",
    cancelButtonText = "Abbrechen",
    confirmButtonColor = "primary",
    showReasonField = false,
    reasonLabel = "Begründung",
    reasonRequired = true,
    children,
    maxWidth = "sm",
    fullWidth = true,
    ...props
}) => {
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;
    const [reason, setReason] = useState('');

    const handleReasonChange = (event) => {
        setReason(event.target.value);
    };

    const handleConfirm = () => {
        // Pass all child elements' states to parent
        const childStates = {};
        React.Children.forEach(children, (child) => {
            if (React.isValidElement(child) && child.props?.control?.props?.checked !== undefined) {
                childStates.doDeleteLinkedItem = child.props.control.props.checked;
            }
        });
        
        onConfirm(true, reason, childStates.doDeleteLinkedItem);
        setReason(''); // Clear reason after confirmation
    };

    const handleCancel = () => {
        onClose(false);
        setReason(''); // Clear reason when canceling
    };

    // Bestimmung, ob der Bestätigungsbutton deaktiviert werden soll
    const isConfirmDisabled = showReasonField && reasonRequired && reason.trim() === '';

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            PaperProps={{
                sx: {
                    borderRadius: isClassic ? '8px' : '12px', // Leicht vergrößerter Radius im klassischen Theme
                    overflow: 'hidden'
                }
            }}
            {...props}
        >
            {/* Beide Themes nutzen jetzt farbige Titelleisten */}
            <DialogTitle sx={{
                backgroundColor: isClassic ? 'designBlue.main' : 'primary.main', // Helleres Blau für Classic-Theme
                color: '#fff',
            }}>
                {title}
            </DialogTitle>

            <DialogContent sx={{ mt: 2, pt: 2, pb: showReasonField ? 1 : 2 }}>
                {description && <DialogContentText>{description}</DialogContentText>}

                {/* Benutzerdefinierter Inhalt */}
                {children}

                {/* Optionales Begründungsfeld */}
                {showReasonField && (
                    <TextField
                        autoFocus
                        margin="dense"
                        label={reasonLabel}
                        type="text"
                        fullWidth
                        value={reason}
                        onChange={handleReasonChange}
                        error={reasonRequired && reason.trim() === ''}
                        helperText={reasonRequired && reason.trim() === '' ? "Dieses Feld ist erforderlich" : ""}
                        sx={{ mt: 2 }}
                    />
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, pt: 1 }}>
                <Button onClick={handleCancel}>{cancelButtonText}</Button>
                <Button
                    onClick={handleConfirm}
                    color={confirmButtonColor}
                    variant="contained"
                    disabled={isConfirmDisabled}
                >
                    {confirmButtonText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const DrkTransportDesignBox = ({ children, isExpertMode, minHeight, ...props }) => {
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;
    const isDark = currentTheme === THEME_DARK;
    const showTopBorder = false;
    return (
        <Box
            mt={isExpertMode ? 0 : 4}
            mb={2}
            pt={isExpertMode ? 2 : 12}
            pl={isExpertMode ? 2 : 4}
            pr={isExpertMode ? 2 : 4}
            pb={isExpertMode ? 2 : 4}
            sx={{
                bgcolor: isDark ? 'background.paper' : undefined,
                background: isDark ? 'none' : 'linear-gradient(180deg, #FFFFFF 0%, #F8F8F8 50%, #F5F5F5 100%)', // Heller Verlauf nur in Light Themes
                borderRadius: isClassic ? '8px' : '16px', // Modernerer Radius im klassischen Design
                width: isExpertMode ? 'auto' : '100%',
                border: isDark
                    ? '1px solid rgba(255, 255, 255, 0.12)'
                    : (isClassic ? '1px solid rgba(91, 155, 213, 0.1)' : '1px solid rgba(228, 100, 80, 0.1)'), // Farbige Border in Light Themes
                boxShadow: isDark
                    ? '0 4px 12px rgba(0, 0, 0, 0.5)'
                    : (isClassic ? '0 2px 6px rgba(0, 0, 0, 0.02)' : '0 4px 12px rgba(0, 0, 0, 0.03)'), // Leichter Schatten in Light
                position: 'relative',
                zIndex: 1,
                transition: 'all 0.2s ease-in-out', // Smooth transition für Hover-Effekte
                '&:hover': {
                    boxShadow: isDark ? '0 6px 16px rgba(0, 0, 0, 0.6)' : (isClassic ? '0 3px 8px rgba(0, 0, 0, 0.03)' : '0 6px 16px rgba(0, 0, 0, 0.05)'),
                },
            }}
            minHeight={minHeight}
            {...props}
        >
            {/* Dekorativer Akzent am oberen Rand - in beiden Themes anzeigen, wenn im Expertenmodus */}
            {isExpertMode && showTopBorder && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: '5%',
                        width: '90%',
                        height: '4px',
                        background: isClassic
                            ? 'linear-gradient(90deg, rgba(46, 117, 182, 0.8) 0%, rgba(46, 117, 182, 0.4) 100%)' // Kontrastreicheres Blau für Classic
                            : 'linear-gradient(90deg, rgba(228, 100, 80, 0.8) 0%, rgba(228, 100, 80, 0.4) 100%)', // Rot für Modern
                        borderRadius: '0 0 4px 4px',
                    }}
                />
            )}
            {children}
        </Box>
    );
};

export const DrkBooleanButtonGroup = ({
    name,
    fieldName,
    label,
    options,
    formData,
    setFormData,
    ...props
}) => {
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;

    const handleChange = (value) => {
        setFormData((prevData) => ({
            ...prevData,
            [fieldName]: value,
        }));
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>{label}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
                {options.map((option) => (
                    <Button
                        key={option.value}
                        variant={formData[fieldName] === option.value ? 'contained' : 'outlined'}
                        color="primary"
                        onClick={() => handleChange(option.value)}
                        sx={{
                            borderRadius: isClassic ? '8px' : '8px', // Gleicher Radius für beide Themes
                            textTransform: 'none',
                        }}
                        {...props}
                    >
                        {option.label}
                    </Button>
                ))}
            </Box>
        </Box>
    );
};

export const DrkAutocomplete = ({
    options,
    getOptionLabel,
    onChange,
    label,
    error,
    helperText,
    ...props
}) => {
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;

    return (
        <MuiAutocomplete
            options={options}
            getOptionLabel={getOptionLabel}
            onChange={onChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    error={error}
                    helperText={helperText}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: isClassic ? '8px' : '8px', // Gleicher Radius für beide Themes
                        },
                    }}
                />
            )}
            {...props}
        />
    );
};

export const DrkLinkButton = ({
    children,
    onClick,
    disabled = false,
    color = 'primary',
    variant = 'body2',
    underlineOnHover = true,
    startIcon = null,
    endIcon = null,
    sx = {},
    ...props
}) => {
    // const { currentTheme } = useThemeContext();
    //const isClassic = currentTheme === THEME_CLASSIC;

    // Basis-Styling
    const baseStyles = {
        cursor: disabled ? 'default' : 'pointer',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        opacity: disabled ? 0.6 : 1,
        fontWeight: 500,
        transition: 'all 0.2s ease',
        ...sx
    };

    // Hover-Effekt hinzufügen, wenn nicht deaktiviert
    if (underlineOnHover && !disabled) {
        baseStyles['&:hover'] = {
            textDecoration: 'underline',
            opacity: 0.9 // Leicht abgedunkelter Hover-Effekt für beide Themes
        };
    }

    // Klick-Handler mit Disabled-Prüfung
    const handleClick = (event) => {
        if (!disabled && onClick) {
            onClick(event);
        }
    };

    return (
        <Typography
            variant={variant}
            color={disabled ? 'text.disabled' : color}
            onClick={handleClick}
            sx={baseStyles}
            {...props}
        >
            {startIcon && <span style={{ marginRight: '4px', display: 'flex' }}>{startIcon}</span>}
            {children}
            {endIcon && <span style={{ marginLeft: '4px', display: 'flex' }}>{endIcon}</span>}
        </Typography>
    );
};

/**
 * DrkHeaderButtonGroup - Standardisierte Komponente für Header-Button-Gruppen
 */
export const DrkHeaderButtonGroup = ({
    buttons,
    position = 'topRight',
    spacing = 1,
    ...props
}) => {

    // Positionierungsstile basierend auf dem übergebenen Parameter
    const positionStyles = {
        topRight: { position: 'absolute', top: 16, right: 16 },
        topLeft: { position: 'absolute', top: 16, left: 16 },
        bottomRight: { position: 'absolute', bottom: 16, right: 16 },
        bottomLeft: { position: 'absolute', bottom: 16, left: 16 },
        inline: { display: 'inline-flex' }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: spacing,
                ...positionStyles[position],
                ...props.sx
            }}
        >
            {buttons}
        </Box>
    );
};

/**
 * DrkHeaderIconButton - Standardisierter IconButton für Headers
 */
/**
 * DrkHeaderIconButton - Standardisierter IconButton für Headers mit verbessertem Design
 */
/**
 * DrkHeaderIconButton - Standardisierter IconButton für Headers mit verbessertem Design
 */
export const DrkHeaderIconButton = ({
    icon,
    tooltip,
    onClick,
    size = 'medium',
    color = 'primary',
    bgColor, // Neuer Prop für benutzerdefinierte Hintergrundfarbe
    ...props
}) => {
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;
    const theme = useTheme();

    // Standard-Hintergrundfarben, falls keine benutzerdefinierte angegeben wurde
    const defaultBackgroundColor = isClassic
        ? 'rgba(91, 155, 213, 0.15)' // Leichteres Blau für Classic
        : theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.25)' // Weiß mit 25% opacity für Dark Mode
            : 'rgba(228, 100, 80, 0.12)'; // Leichteres Rot für Modern

    // Tatsächliche Hintergrundfarbe - entweder benutzerdefiniert oder Standard
    const backgroundColor = bgColor || defaultBackgroundColor;

    // Standard-Hover-Effekt
    const defaultHoverBackgroundColor = isClassic
        ? 'rgba(91, 155, 213, 0.25)'
        : theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.35)' // Weiß mit 35% opacity Hover für Dark Mode
            : 'rgba(228, 100, 80, 0.2)';

    // Benutzerdefinierter Hover-Effekt (leicht abgedunkelt)
    const customHoverBackgroundColor = bgColor
        ? (typeof bgColor === 'string' && bgColor.startsWith('rgba'))
            // Bei RGBA: Erhöhen des Alpha-Werts
            ? bgColor.replace(/rgba\((\d+,\s*\d+,\s*\d+,\s*)[\d.]+\)/, `rgba($1${Math.min(parseFloat(bgColor.match(/rgba\(\d+,\s*\d+,\s*\d+,\s*([\d.]+)\)/)[1]) + 0.1, 1)})`)
            // Bei HEX oder anderen: Leichter verdunkeln
            : bgColor
        : defaultHoverBackgroundColor;

    // Aktiver Zustand
    const defaultActiveBackgroundColor = isClassic
        ? 'rgba(91, 155, 213, 0.35)'
        : theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.45)' // Weiß mit 45% opacity Active für Dark Mode
            : 'rgba(228, 100, 80, 0.3)';

    // Benutzerdefinierter Active-Effekt (stärker abgedunkelt)
    const customActiveBackgroundColor = bgColor
        ? (typeof bgColor === 'string' && bgColor.startsWith('rgba'))
            // Bei RGBA: Stärker erhöhter Alpha-Wert
            ? bgColor.replace(/rgba\((\d+,\s*\d+,\s*\d+,\s*)[\d.]+\)/, `rgba($1${Math.min(parseFloat(bgColor.match(/rgba\(\d+,\s*\d+,\s*\d+,\s*([\d.]+)\)/)[1]) + 0.2, 1)})`)
            // Bei HEX oder anderen: Stärker verdunkeln
            : bgColor
        : defaultActiveBackgroundColor;

    // Icon-Größen
    const iconSize = {
        small: 18,
        medium: 22,
        large: 28
    };

    // Icon-Farbe basierend auf dem Theme (oder benutzerdefiniert)
    const defaultIconColor = isClassic
        ? '#2E75B6' // Dunkleres Blau für das Icon im Classic-Theme
        : theme.palette.mode === 'dark'
            ? '#ffffff' // Weiß für das Icon im Dark Mode (auf orangem Header)
            : '#eb5b28'; // DRK-Rot für das Icon im modernen Light Mode

    // Tatsächliche Icon-Farbe - wir verwenden den color-Prop für die Icon-Farbe
    const iconColor = color === 'primary' ? defaultIconColor : color;

    // Wrap disabled buttons in span to make tooltip work
    const button = (
        <IconButton
            onClick={onClick}
            size={size}
            sx={{
                bgcolor: backgroundColor,
                borderRadius: '50%',
                width: size === 'small' ? 32 : size === 'medium' ? 40 : 48,
                height: size === 'small' ? 32 : size === 'medium' ? 40 : 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease-in-out',
                border: isClassic
                    ? '1px solid rgba(91, 155, 213, 0.2)'
                    : theme.palette.mode === 'dark'
                        ? '1px solid rgba(255, 255, 255, 0.3)' // Weiß Border für Dark Mode
                        : '1px solid rgba(228, 100, 80, 0.15)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                '&:hover': {
                    bgcolor: customHoverBackgroundColor,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                },
                '&:active': {
                    bgcolor: customActiveBackgroundColor,
                    transform: 'translateY(0)',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                },
                '&.Mui-disabled': {
                    opacity: 0.5,
                    bgcolor: backgroundColor,
                },
                ...props.sx
            }}
            {...props}
        >
            {React.cloneElement(icon, {
                sx: {
                    fontSize: iconSize[size],
                    color: iconColor
                }
            })}
        </IconButton>
    );

    return (
        <Tooltip title={tooltip}>
            {props.disabled ? <span>{button}</span> : button}
        </Tooltip>
    );
};

/**
 * DrkSearchField - Spezialisiertes Suchfeld mit aktivem Styling
 */
export const DrkSearchField = ({
    value,
    onChange,
    onClear,
    placeholder = "Suchen...",
    size = "small",
    fullWidth = false,
    sx = {},
    InputProps = {},
    ...props
}) => {
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;
    const isActive = value && value.length > 0;

    const handleChange = (e) => {
        if (onChange) onChange(e.target.value);
    };

    const handleClear = () => {
        if (onClear) onClear();
    };

    // Hintergrundfarbe basierend auf dem Theme
    const bgColor = isActive
        ? isClassic
            ? 'rgba(91, 155, 213, 0.1)' // Helleres Blau für Classic
            : 'rgba(228, 100, 80, 0.1)' // Leichtes Orange für Modern
        : 'inherit';

    return (
        <TextField
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            variant="outlined"
            size={size}
            fullWidth={fullWidth}
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: '20px',
                    backgroundColor: bgColor,
                    transition: 'background-color 0.2s',

                    '& fieldset': {
                        borderColor: isActive ? 'primary.main' : 'lightgray',
                        borderRadius: '20px',
                    },
                    '&:hover fieldset': {
                        borderColor: isActive ? 'primary.main' : 'lightgray',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                    },
                },
                ...sx
            }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon color={isActive ? "primary" : "action"} />
                    </InputAdornment>
                ),
                endAdornment: isActive ? (
                    <InputAdornment position="end">
                        <IconButton
                            aria-label="clear search"
                            onClick={handleClear}
                            edge="end"
                            size="small"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </InputAdornment>
                ) : null,
                ...InputProps
            }}
            {...props}
        />
    );
};

/**
 * DrkFilterSelect - Spezialisiertes Select für Filter mit aktivem Styling
 */
export const DrkFilterSelect = ({
    label,
    value,
    onChange,
    options = [],
    defaultValue = 'all',
    allLabel = 'Alle',
    fullWidth = false,
    size = "small",
    sx = {},
    ...props
}) => {
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;
    const isActive = value !== defaultValue;

    return (
        <FormControl
            fullWidth={fullWidth}
            size={size}
            sx={{
                minWidth: props.minWidth || 150,
                ...sx
            }}
        >
            <InputLabel id={`${label}-filter-label`}>{label}</InputLabel>
            <Select
                labelId={`${label}-filter-label`}
                value={value}
                label={label}
                onChange={onChange}
                sx={getActiveFilterStyle(isActive, isClassic)}
                {...props}
            >
                <MenuItem value={defaultValue}>{allLabel}</MenuItem>
                {options.map((option) => (
                    <MenuItem key={option.value || option} value={option.value || option}>
                        {option.label || option}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

/**
 * DrkPasswordRequirements - Zeigt Passwort-Anforderungen mit Validierungsstatus an
 */
export const DrkPasswordRequirements = ({ password = '', showRequirements = true, sx = {} }) => {
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;

    const requirements = getPasswordRequirementStatus(password);

    const requirementItems = [
        { key: 'minLength', label: 'Mindestens 12 Zeichen', met: requirements.minLength },
        { key: 'hasUpperCase', label: 'Mindestens ein Großbuchstabe (A-Z)', met: requirements.hasUpperCase },
        { key: 'hasLowerCase', label: 'Mindestens ein Kleinbuchstabe (a-z)', met: requirements.hasLowerCase },
        { key: 'hasNumber', label: 'Mindestens eine Zahl (0-9)', met: requirements.hasNumber },
        { key: 'hasSpecialChar', label: 'Mindestens ein Sonderzeichen (!@#$%^&*()_+-={};\':|,.<>?)', met: requirements.hasSpecialChar }
    ];

    if (!showRequirements) return null;

    return (
        <Box sx={{
            mt: 2,
            p: 2,
            borderRadius: isClassic ? '8px' : '12px',
            backgroundColor: isClassic 
                ? 'rgba(91, 155, 213, 0.05)' 
                : 'rgba(228, 100, 80, 0.05)',
            border: `1px solid ${isClassic ? 'rgba(91, 155, 213, 0.2)' : 'rgba(228, 100, 80, 0.2)'}`,
            ...sx
        }}>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                Passwort-Anforderungen:
            </Typography>
            
            {requirementItems.map((item) => (
                <Box key={item.key} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 0.5,
                    minHeight: '24px'
                }}>
                    {password ? (
                        item.met ? (
                            <CheckIcon sx={{ 
                                fontSize: 16, 
                                color: 'success.main', 
                                mr: 1 
                            }} />
                        ) : (
                            <ClearIcon sx={{ 
                                fontSize: 16, 
                                color: 'error.main', 
                                mr: 1 
                            }} />
                        )
                    ) : (
                        <Box sx={{ 
                            width: 16, 
                            height: 16, 
                            borderRadius: '50%',
                            border: '2px solid',
                            borderColor: 'text.disabled',
                            mr: 1
                        }} />
                    )}
                    
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: password 
                                ? item.met 
                                    ? 'success.main' 
                                    : 'error.main'
                                : 'text.secondary',
                            fontWeight: password && item.met ? 500 : 400
                        }}
                    >
                        {item.label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

/**
 * DrkTableSection - Einheitlicher Container für Tabellen mit Überschrift
 *
 * Verwendet Paper als Hintergrund mit abgerundeten Ecken und Padding.
 * Zeigt optional eine Überschrift mit Anzahl der Einträge an.
 *
 * @param {string} title - Überschrift für die Tabelle
 * @param {number} count - Optional: Anzahl der Einträge (wird als "(X Einträge)" angezeigt)
 * @param {React.ReactNode} children - Inhalt (typischerweise DrkDataGrid)
 * @param {object} sx - Zusätzliche Style-Props
 */
export const DrkTableSection = ({
    title,
    count,
    children,
    sx = {}
}) => {
    return (
        <Paper sx={{ p: 2, ...sx }}>
            {title && (
                <Typography variant="h6" gutterBottom>
                    {title}
                    {count !== undefined && ` (${count} Einträge)`}
                </Typography>
            )}
            {children}
        </Paper>
    );
};

const DrkComponents = {
    DrkPageContainer,
    DrkCard,
    DrkIconCircle,
    DrkInfoBox,
    DrkLoginLink,
    DrkDialogCard,
    DrkDataGrid,
    DrkFormField,
    DrkActionBar,
    DrkPreviewFrame,
    DrkDialog,
    DrkConfirmationDialog,
    DrkTransportDesignBox,
    DrkBooleanButtonGroup,
    DrkAutocomplete,
    DrkLinkButton,
    DrkHeaderButtonGroup,
    DrkHeaderIconButton,
    DrkSearchField,
    DrkFilterSelect,
    DrkMultiFilterSelect,
    DrkPasswordRequirements,
    DrkTableSection,
    getActiveFilterStyle,
};

export default DrkComponents;
